/**
 * Document Processing Pipeline
 *
 * Converts Y.Doc rich-text content into:
 * - `docHtml` — HTML string for icon indicators (has-note?) and export
 * - `fullTextIndex` — keyword array for multi-entry search index
 *
 * Two processing paths:
 * 1. **Editor hot path** (Layer 1): EditorShell saves docHtml + fullTextIndex
 *    on debounced onUpdate and on editor destroy.
 * 2. **Background processor** (Layer 2): defineYDocTrigger fires on Y.Doc sync
 *    from other devices → markDirty() → in-memory Set → batch process.
 *
 * Startup cursor-sweep ensures no documents are missed after reload/crash/first-login.
 *
 * @see docs/DOC_PROCESSING_DESIGN.md for full architecture
 */

// --- Public API ---
export { yDocToHtml, isEmptyDocHtml } from "./yDocToHtml"
export { extractSearchKeywords, htmlToPlainText } from "./extractSearchKeywords"
export { docProcessor } from "./docProcessor"
export { cleanOrphanedFiles } from "./fileGC"

// --- Internal (used by integration points) ---
import { defineYDocTrigger } from "dexie-cloud-addon"
import { db } from "@/db/schema"
import { docProcessor } from "./docProcessor"
import { sweepStaleDocuments } from "./startupSweep"

/**
 * Initialize the document processing pipeline.
 *
 * Call once after initial sync completes (waitForInitialSync resolved).
 *
 * 1. Registers defineYDocTrigger — catches all future Y.Doc mutations
 *    from sync and calls `docProcessor.markDirty(id)`.
 * 2. Runs the cursor-based startup sweep — scans `$totodo.doc_updates`
 *    for documents changed since last session and feeds them to the processor.
 *
 * The trigger is registered BEFORE the sweep so that any Y.Doc changes
 * arriving during the sweep are captured by the trigger and deduplicated
 * by the processor's in-memory Set.
 */
export function initDocProcessing(): void {
  // 1. Register Y.Doc trigger (catches real-time sync going forward)
  defineYDocTrigger(db.totodo, "doc", (_yDoc, parentId) => {
    docProcessor.markDirty(parentId as string)
  })

  // 2. Cursor-sweep for changes since last session
  void sweepStaleDocuments()
}
